import React, { useState } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import PartnerPresenceIndicator from './PartnerPresenceIndicator';

const PartnerVerificationStep = ({ formData, errors, onChange }) => {
  const [verificationMethod, setVerificationMethod] = useState('email');
  const [codeSent, setCodeSent] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [partnerConnected, setPartnerConnected] = useState(false);

  const handleSendCode = () => {
    setCodeSent(true);
    setIsVerifying(true);
    
    // Simulate partner connection after verification
    setTimeout(() => {
      setPartnerConnected(true);
      setIsVerifying(false);
    }, 3000);
  };

  const handleVerifyCode = () => {
    setIsVerifying(true);
    
    // Simulate partner connection after code verification
    setTimeout(() => {
      setPartnerConnected(true);
      setIsVerifying(false);
    }, 2000);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-heading font-semibold text-foreground mb-2">
          Connect with Your Partner
        </h2>
        <p className="text-sm md:text-base text-muted-foreground">
          Verify your relationship by connecting with your partner's account.
        </p>
      </div>
      {/* Real-time Presence Indicator */}
      {(codeSent || formData?.verificationCode?.length >= 6) && (
        <PartnerPresenceIndicator 
          partnerName={formData?.partnerEmail?.split('@')?.[0] || 'Partner'}
          isConnected={partnerConnected}
        />
      )}
      <div className="bg-card border border-border rounded-lg p-4 md:p-6">
        <h3 className="text-base md:text-lg font-heading font-semibold text-foreground mb-4">
          Choose Verification Method
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-6">
          <button
            type="button"
            onClick={() => setVerificationMethod('email')}
            className={`
              flex items-center space-x-3 p-4 rounded-lg border-2 transition-therapeutic
              ${verificationMethod === 'email' ?'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/50'
              }
            `}
          >
            <Icon 
              name="Mail" 
              size={24} 
              color={verificationMethod === 'email' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
            />
            <div className="flex-1 text-left">
              <h4 className="text-sm md:text-base font-medium text-foreground">
                Email Invitation
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Send invite to partner's email
              </p>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setVerificationMethod('code')}
            className={`
              flex items-center space-x-3 p-4 rounded-lg border-2 transition-therapeutic
              ${verificationMethod === 'code' ?'border-primary bg-primary/5' :'border-border bg-background hover:border-primary/50'
              }
            `}
          >
            <Icon 
              name="Key" 
              size={24} 
              color={verificationMethod === 'code' ? 'var(--color-primary)' : 'var(--color-muted-foreground)'}
            />
            <div className="flex-1 text-left">
              <h4 className="text-sm md:text-base font-medium text-foreground">
                Shared Code
              </h4>
              <p className="text-xs md:text-sm text-muted-foreground">
                Use partner's verification code
              </p>
            </div>
          </button>
        </div>

        {verificationMethod === 'email' && (
          <div className="space-y-4">
            <Input
              label="Partner's Email Address"
              type="email"
              name="partnerEmail"
              placeholder="partner@example.com"
              description="We'll send them an invitation to join and verify your relationship"
              value={formData?.partnerEmail}
              onChange={onChange}
              error={errors?.partnerEmail}
              required
            />

            {!codeSent ? (
              <Button
                variant="outline"
                iconName="Send"
                iconPosition="left"
                onClick={handleSendCode}
                fullWidth
              >
                Send Invitation Email
              </Button>
            ) : (
              <>
                <div className="bg-success/10 border border-success/20 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <Icon name="CheckCircle" size={20} color="var(--color-success)" className="flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-foreground">
                        <strong>Invitation sent!</strong> Your partner will receive an email with instructions to complete their onboarding.
                      </p>
                      {isVerifying && (
                        <p className="text-xs text-muted-foreground mt-2">
                          Waiting for partner to accept invitation...
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {verificationMethod === 'code' && (
          <div className="space-y-4">
            <div className="bg-accent/10 border border-accent/20 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} color="var(--color-accent)" className="flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs md:text-sm text-foreground">
                    If your partner has already started their onboarding, they can share their verification code with you. You'll find this code in their onboarding process.
                  </p>
                </div>
              </div>
            </div>

            <Input
              label="Partner's Verification Code"
              type="text"
              name="verificationCode"
              placeholder="Enter 6-digit code"
              description="Ask your partner for their verification code"
              value={formData?.verificationCode}
              onChange={(e) => {
                onChange(e);
                if (e?.target?.value?.length === 6) {
                  handleVerifyCode();
                }
              }}
              error={errors?.verificationCode}
              maxLength={6}
              required
            />

            {isVerifying && formData?.verificationCode?.length >= 6 && (
              <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
                <div className="flex items-center space-x-2">
                  <Icon name="RefreshCw" size={16} color="var(--color-accent)" className="animate-spin" />
                  <p className="text-sm text-foreground">
                    Verifying code and establishing connection...
                  </p>
                </div>
              </div>
            )}

            <div className="bg-muted rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-foreground">Your Verification Code</span>
                <Button variant="ghost" size="sm" iconName="Copy">
                  Copy
                </Button>
              </div>
              <div className="bg-background rounded-md p-3 text-center">
                <span className="text-2xl md:text-3xl font-mono font-semibold text-primary tracking-wider">
                  {formData?.myVerificationCode || '------'}
                </span>
              </div>
              <p className="text-xs text-muted-foreground mt-2">
                Share this code with your partner if they need to verify with you
              </p>
            </div>
          </div>
        )}
      </div>
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 md:p-5">
        <div className="flex items-start space-x-3">
          <Icon name="Users" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <h3 className="text-sm md:text-base font-medium text-foreground mb-2">
              Why Verification?
            </h3>
            <p className="text-xs md:text-sm text-muted-foreground">
              Verification ensures that both partners are genuine and committed to using LoveNudge together. This creates a safe, trusted environment for intimate communication.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerVerificationStep;